import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { Grid, Paper, Typography, Box } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { BarChart3, PieChart } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    display: "flex",
    flexDirection: "column"
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.text.primary
  },
  chartContainer: {
    flex: 1,
    position: "relative",
    minHeight: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  emptyNotice: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  }
}));

const ChartsSection = ({ dailyHistory = [], byCategory = {}, currency = "USD" }) => {
  const classes = useStyles();
  const theme = useTheme();

  // 1. Dados para o gráfico de evolução diária (Barra/Volume)
  const sortedDays = [...dailyHistory].slice(-30);
  const barLabels = sortedDays.map((d) => {
    const parts = d.date.split("-");
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d.date;
  });

  const barData = {
    labels: barLabels,
    datasets: [
      {
        label: "Marketing",
        data: sortedDays.map((d) => d.categories?.MARKETING?.volume || 0),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderRadius: 4
      },
      {
        label: "Serviço",
        data: sortedDays.map((d) => d.categories?.SERVICE?.volume || 0),
        backgroundColor: "rgba(16, 185, 129, 0.8)",
        borderRadius: 4
      },
      {
        label: "Utilidade",
        data: sortedDays.map((d) => d.categories?.UTILITY?.volume || 0),
        backgroundColor: "rgba(245, 158, 11, 0.8)",
        borderRadius: 4
      },
      {
        label: "Autenticação",
        data: sortedDays.map((d) => d.categories?.AUTHENTICATION?.volume || 0),
        backgroundColor: "rgba(139, 92, 246, 0.8)",
        borderRadius: 4
      }
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: theme.palette.text.primary,
          boxWidth: 12,
          usePointStyle: true,
          font: { size: 11 }
        }
      },
      tooltip: {
        callbacks: {
          footer: (items) => {
            if (!items.length) return "";
            const idx = items[0].dataIndex;
            const dayCost = sortedDays[idx]?.totalCost || 0;
            return `Gasto no dia: ${currency} ${dayCost.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
        grid: { display: false }
      },
      y: {
        stacked: true,
        ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
        grid: { color: theme.palette.divider }
      }
    }
  };

  // 2. Dados para o gráfico de rosca (Distribuição de Custos por Categoria)
  const doughnutLabels = ["Marketing", "Serviço", "Utilidade", "Autenticação"];
  const doughnutValues = [
    byCategory?.MARKETING?.cost || 0,
    byCategory?.SERVICE?.cost || 0,
    byCategory?.UTILITY?.cost || 0,
    byCategory?.AUTHENTICATION?.cost || 0
  ];
  const hasDoughnutData = doughnutValues.some((v) => v > 0);

  const doughnutData = {
    labels: doughnutLabels,
    datasets: [
      {
        data: doughnutValues,
        backgroundColor: [
          "rgba(59, 130, 246, 0.85)",
          "rgba(16, 185, 129, 0.85)",
          "rgba(245, 158, 11, 0.85)",
          "rgba(139, 92, 246, 0.85)"
        ],
        borderWidth: 2,
        borderColor: theme.palette.background.paper
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: theme.palette.text.primary,
          boxWidth: 12,
          usePointStyle: true,
          font: { size: 11 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.raw || 0;
            return ` ${context.label}: ${currency} ${Number(val).toFixed(2)}`;
          }
        }
      }
    },
    cutout: "68%"
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <Paper className={classes.paper} elevation={0}>
          <Box className={classes.header}>
            <BarChart3 size={20} color={theme.palette.primary.main} />
            <Typography className={classes.title}>
              Evolução Diária de Mensagens por Categoria
            </Typography>
          </Box>
          <Box className={classes.chartContainer}>
            {sortedDays.length > 0 ? (
              <Bar data={barData} options={barOptions} />
            ) : (
              <Typography className={classes.emptyNotice}>
                Nenhum dado diário disponível para o período selecionado.
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper className={classes.paper} elevation={0}>
          <Box className={classes.header}>
            <PieChart size={20} color={theme.palette.primary.main} />
            <Typography className={classes.title}>
              Distribuição de Gastos ({currency})
            </Typography>
          </Box>
          <Box className={classes.chartContainer}>
            {hasDoughnutData ? (
              <Doughnut data={doughnutData} options={doughnutOptions} />
            ) : (
              <Typography className={classes.emptyNotice}>
                Nenhum custo registrado para o período.
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ChartsSection;
